import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import PropTypes from 'prop-types';

import { Button } from 'react-bootstrap';
import Header from '../../../components/Header/Header';
import { pageTitle } from '../../../util/pageTitles';
import { setSuccessMessage } from '../../App/ErrorsActions';
import { TOKEN_PLACEHOLDER } from '../docsConstants';
import DocsLinks from '../components/DocsLinks';
import mcpIcon from '../../../assets/img/mcp.svg';
import linkIcon from '../../../assets/img/link.svg';
import accessToken from '../../../assets/img/access-token.svg';
import code from '../../../assets/img/code.svg';

// Import Style
import './McpDocsPage.scss';

const MCP_URL = `${process.env.MCP_URL || 'https://mcp.nave.local:8084'}/mcp`;

const getMcpConfig = (token) => JSON.stringify({
  mcpServers: {
    'nave-mcp': {
      type: 'http',
      url: MCP_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  },
}, null, 2);

const getMcpCliCommand = (token) => `claude mcp add --transport http nave-mcp ${MCP_URL} --header "Authorization: Bearer ${token}"`;

export class McpDocsPage extends Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    setSuccessMessage: PropTypes.func.isRequired,
  };

  pageRef = React.createRef();

  componentDidMount() {
    this.pageRef.current.scrollIntoView();
  }

  handleCopiedUrl = () => {
    this.props.setSuccessMessage('MCP server URL copied to your clipboard');
  };

  handleCopiedToken = () => {
    this.props.setSuccessMessage('Nave access token copied to your clipboard');
  };

  handleCopiedConfig = () => {
    this.props.setSuccessMessage('MCP server configuration copied to your clipboard');
  };

  handleCopiedCliCommand = () => {
    this.props.setSuccessMessage('Claude Code command copied to your clipboard');
  };

  render() {
    const { user } = this.props;

    return (
      <div className='mcp_docs' ref={this.pageRef}>
        <Helmet>
          <title>{pageTitle('MCP Server')}</title>
        </Helmet>
        <Header title="" showHome={true} />
        <div className='mcp_docs_scroll'>
          <div className='mcp_docs_wrap'>

            <div className='mcp_docs_intro'>
              <DocsLinks current='mcp' />
              <div className="mcp_docs_intro_image">
                <img alt="mcp icon" src={mcpIcon} className='mcp_docs_intro_icon' />
              </div>
              <h1>Nave MCP Server</h1>
              <p className="mcp_docs_intro_text">
                Connect any MCP-compatible AI assistant to your Nave data using the Model Context Protocol (MCP) over HTTP. Once configured, it can list your dashboards and query dashboard details, metrics, insights, completed cards, and per-stage aging, blockers, percentiles, and statistics.
              </p>
              <div className='mcp_docs_intro_setup'>
                <span className='mcp_docs_intro_setup_label'>Setup guides</span>
                <div className='mcp_docs_intro_setup_links'>
                  <a className='mcp_docs_tag' href="https://support.claude.com/en/articles/11175166-getting-started-with-custom-connectors-using-remote-mcp" target="_blank" rel="noopener noreferrer">Claude</a>
                  <a className='mcp_docs_tag' href="https://code.claude.com/docs/en/mcp" target="_blank" rel="noopener noreferrer">Claude Code</a>
                  <a className='mcp_docs_tag' href="https://code.visualstudio.com/docs/copilot/customization/mcp-servers" target="_blank" rel="noopener noreferrer">VS Code/Copilot</a>
                  <a className='mcp_docs_tag' href="https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt" target="_blank" rel="noopener noreferrer">ChatGPT</a>
                </div>
              </div>
            </div>

            <div className='mcp_docs_content'>
              <div className='mcp_docs_row'>
                <p>Add the Nave MCP Server to your AI assistant using this URL:</p>
                <CopyToClipboard text={MCP_URL} onCopy={this.handleCopiedUrl}>
                  <Button type="button" variant="secondary" size="md">
                    <img src={linkIcon} alt="#" /> Copy MCP URL
                  </Button>
                </CopyToClipboard>
              </div>
              <div className='mcp_docs_row'>
                <p>When prompted for authorization, use your Nave access token:</p>
                <CopyToClipboard text={user.displayToken} onCopy={this.handleCopiedToken}>
                  <Button type="button" variant="secondary" size="md">
                    <img src={accessToken} alt="#" /> Copy access token
                  </Button>
                </CopyToClipboard>
              </div>
              <p className='mcp_docs_content_label'>
                Or, if your client uses a JSON configuration file, copy the snippet below. The copy button replaces YOUR_TOKEN with your Nave access token.
              </p>
              <div className='mcp_docs_code'>
                <div className='mcp_docs_code_header'>
                  <span>JSON configuration</span>
                  <CopyToClipboard text={getMcpConfig(user.displayToken)} onCopy={this.handleCopiedConfig}>
                    <Button type="button" variant="secondary" size="md">
                      <img src={code} alt="#" /> Copy configuration
                    </Button>
                  </CopyToClipboard>
                </div>
                <pre className='mcp_docs_code_block'>{getMcpConfig(TOKEN_PLACEHOLDER)}</pre>
              </div>
              <p className='mcp_docs_content_label'>
                Or, if you're using Claude Code, copy the command below and run it in your terminal. The copy button replaces YOUR_TOKEN with your Nave access token.
              </p>
              <div className='mcp_docs_code'>
                <div className='mcp_docs_code_header'>
                  <span>Claude Code command</span>
                  <CopyToClipboard text={getMcpCliCommand(user.displayToken)} onCopy={this.handleCopiedCliCommand}>
                    <Button type="button" variant="secondary" size="md">
                      <img src={code} alt="#" /> Copy command
                    </Button>
                  </CopyToClipboard>
                </div>
                <pre className='mcp_docs_code_block'>{getMcpCliCommand(TOKEN_PLACEHOLDER)}</pre>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    setSuccessMessage: (msg) => dispatch(setSuccessMessage(msg)),
  };
}

function mapStateToProps(state) {
  return {
    user: state.auth.user,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(McpDocsPage);
